import { createAxiosClient, ServiceProxyMap } from '@atlassian/temp-client-utils';
import { Results } from '@atlassian/cssxp-typescommon';
import {
  Quote,
  QuoteSearchOptions,
  QuoteSearchParams,
  QuoteV2,
  QuoteWithLineItems,
} from '@atlassian/cssxp-types-quote';
import { CommerceNoteSearchParams, CommerceNoteSearchOptions, CommerceNoteResults } from '@atlassian/cssxp-types-audit';
import { base64ToBlob } from '../invoice/invoices';

const client = createAxiosClient({ baseURL: '/api/1/quotes' }, ServiceProxyMap.ANS);
const sxpClient = createAxiosClient({ baseURL: '/api/1/quotes' }, ServiceProxyMap.SXP);

export enum PDFStatus {
  NOT_FOUND = 'NOT_FOUND',
  IN_PROGRESS = 'IN_PROGRESS',
  AVAILABLE_FOR_DOWNLOAD = 'AVAILABLE_FOR_DOWNLOAD',
}

export interface DownloadQuoteResponseDto {
  pdfUrl?: string;
  pdfStatus: PDFStatus;
}

export async function searchQuotes(searchParams: QuoteSearchParams & QuoteSearchOptions): Promise<Results<Quote>> {
  const result = await client.get('/', {
    params: searchParams,
  });

  return result.data;
}

export async function reviseQuote(requestBody: QuoteV2.ReviseQuoteRequest): Promise<any> {
  const result = await sxpClient.post(`/${requestBody.id}/revise`, requestBody);
  return result.data;
}

export async function cloneQuote(requestBody: QuoteV2.CloneQuoteRequest): Promise<any> {
  const result = await sxpClient.post(`/${requestBody.id}/clone`, requestBody);
  return result.data;
}

export async function createSXPQuote(requestBody: QuoteV2.CreateQuoteRequest): Promise<any> {
  const result = await sxpClient.post('/create', requestBody);
  return result.data;
}

export async function finalizeQuote(requestBody: QuoteV2.FinalizeQuoteRequest): Promise<any> {
  const result = await sxpClient.post('/finalize', requestBody);
  return result.data;
}

export async function cancelQuote(requestBody: QuoteV2.CancelQuoteRequest): Promise<any> {
  const result = await sxpClient.post(`/${requestBody.id}/cancel`, requestBody);
  return result.data;
}

export async function updateSXPQuote(requestBody: QuoteV2.CreateQuoteRequest): Promise<any> {
  const result = await sxpClient.put('/update', requestBody);
  return result.data;
}

export async function acceptQuote(requestBody: QuoteV2.AcceptQuoteRequest): Promise<any> {
  const result = await sxpClient.post(`/${requestBody.id}/accept`, requestBody);
  return result.data;
}

export async function updateOpenSXPQuote(requestBody: QuoteV2.CreateQuoteRequest): Promise<any> {
  const result = await sxpClient.patch('/updateOpenQuote', requestBody);
  return result.data;
}

export async function fetchQuote(id: string): Promise<QuoteWithLineItems> {
  const result = await client.get(`/${id}`);
  return result.data;
}

export async function searchCommerceNotesQuotes(
  params: CommerceNoteSearchParams & CommerceNoteSearchOptions
): Promise<CommerceNoteResults> {
  const { quoteKey, ...remainingSearchParams } = params;

  const response = await client.get(`/${encodeURIComponent(quoteKey!)}/notes`, {
    params: remainingSearchParams,
  });
  return response.data;
}

export async function downloadQuotePdf(id: string, transactionAccountId: string | null): Promise<string | null> {
  let fileUrl = null;
  try {
    const response = await sxpClient.post(`/${id}/download`, {
      transactionAccountId,
    });
    if (response && response.data && response.data.successful) {
      const blob = response.data.base64String ? base64ToBlob(response.data.base64String) : null;
      fileUrl = blob ? (window.URL || window.webkitURL).createObjectURL(blob) : null;
    }

    return fileUrl;
  } catch {
    return null;
  }
}

export async function downloadQuotePdfV2(id: string, transactionAccountId: string): Promise<DownloadQuoteResponseDto> {
  const response = await sxpClient.post(`/${id}/v2/download`, {
    transactionAccountId,
  });

  return response.data;
}

export async function fixDraftQuote(requestBody: QuoteV2.CreateQuoteRequest): Promise<any> {
  const result = await sxpClient.post(`/${requestBody.id}/draft`, requestBody);
  return result.data;
}
